const MIN_SCHEDULE_YEAR = 1900;
const MAX_SCHEDULE_YEAR = 2100;

const pad2 = (n) => String(n).padStart(2, '0');

const isValidYmd = (year, month, day) => {
  if (year < MIN_SCHEDULE_YEAR || year > MAX_SCHEDULE_YEAR) return false;
  const d = new Date(year, month - 1, day);
  return (
    d.getFullYear() === year &&
    d.getMonth() === month - 1 &&
    d.getDate() === day
  );
};

const toIsoDateString = (year, month, day) =>
  `${year}-${pad2(month)}-${pad2(day)}`;

/** Минимальный/максимальный серийный номер Excel для дат графиков */
const EXCEL_SERIAL_MIN = 1;
const EXCEL_SERIAL_MAX = 500000;

/**
 * Нормализует текст из буфера обмена (Excel: первая ячейка, без времени и лишних пробелов).
 */
export const normalizeDatePasteText = (value) => {
  if (value == null) return '';
  let s = String(value).replace(/\u00a0/g, ' ').trim();
  if (!s) return '';

  s = s.split(/\r?\n/)[0].trim();
  if (s.includes('\t')) {
    s = s.split('\t')[0].trim();
  }

  s = s.replace(/[T ]\d{1,2}:\d{2}(:\d{2})?(\.\d+)?.*$/i, '');
  return s.trim();
};

/**
 * Дата из серийного номера Excel (Windows, 1900 date system).
 * @returns {string|null} YYYY-MM-DD
 */
export const parseExcelSerialToIso = (serial) => {
  const n = Math.round(Number(serial));
  if (!Number.isFinite(n) || n < EXCEL_SERIAL_MIN || n > EXCEL_SERIAL_MAX) {
    return null;
  }
  const base = new Date(1899, 11, 30);
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + n);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  return isValidYmd(year, month, day) ? toIsoDateString(year, month, day) : null;
};

/**
 * Строгий разбор даты: ручной ввод, вставка из Excel (текст и серийный номер).
 * @returns {string|null} YYYY-MM-DD или null
 */
export const parseUserDateInput = (value) => {
  if (value == null || value === '') return null;
  const s = normalizeDatePasteText(value);
  if (!s) return null;

  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    return isValidYmd(year, month, day) ? toIsoDateString(year, month, day) : null;
  }

  m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    const year = Number(m[3]);
    return isValidYmd(year, month, day) ? toIsoDateString(year, month, day) : null;
  }

  m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2})(?:\D|$)/);
  if (m) {
    const day = Number(m[1]);
    const month = Number(m[2]);
    let year = Number(m[3]);
    year = year < 70 ? 2000 + year : 1900 + year;
    return isValidYmd(year, month, day) ? toIsoDateString(year, month, day) : null;
  }

  const numeric = s.replace(/\s/g, '').replace(',', '.');
  if (/^\d+(\.\d+)?$/.test(numeric)) {
    return parseExcelSerialToIso(numeric);
  }

  return null;
};

/**
 * Парсит последнюю дату из строки с несколькими датами
 * @param {string} dateString - Строка с датами, например "06.05.2025\n30.05.2025\n04.06.2025-\nконцепция\n15.07.2025\n15.08.2025"
 * @returns {Date|null} - Последняя валидная дата или null
 */
export const parseLastDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  // Разбиваем строку на строки и очищаем от лишних символов
  const lines = dateString.split('\n').map(line => line.trim()).filter(line => line);
  
  const dates = [];
  
  for (const line of lines) {
    // Ищем даты в формате DD.MM.YYYY
    const dateMatches = line.match(/\b(\d{1,2})\.(\d{1,2})\.(\d{4})\b/g);
    
    if (dateMatches) {
      for (const dateMatch of dateMatches) {
        const [day, month, year] = dateMatch.split('.').map(num => parseInt(num, 10));
        
        // Проверяем валидность даты
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() === year && 
            date.getMonth() === month - 1 && 
            date.getDate() === day) {
          dates.push(date);
        }
      }
    }
  }
  
  // Возвращаем последнюю дату (самую позднюю)
  if (dates.length > 0) {
    dates.sort((a, b) => a.getTime() - b.getTime());
    return dates[dates.length - 1];
  }
  
  return null;
};

/**
 * Форматирует дату для отображения в формате DD/MM/YYYY.
 * Строку YYYY-MM-DD парсит как локальную дату (не UTC).
 */
export const formatDate = (date) => {
  if (!date) return '';

  if (typeof date === 'string') {
    const iso = parseUserDateInput(date);
    if (iso) {
      const [y, mo, d] = iso.split('-').map(Number);
      return `${pad2(d)}/${pad2(mo)}/${y}`;
    }
    return '';
  }

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  return `${pad2(dateObj.getDate())}/${pad2(dateObj.getMonth() + 1)}/${dateObj.getFullYear()}`;
};

/**
 * Вычисляет количество дней до дедлайна
 * @param {Date} deadline - Дата дедлайна
 * @returns {number} - Количество дней до дедлайна (отрицательное, если дедлайн прошел)
 */
export const getDaysUntilDeadline = (deadline) => {
  if (!deadline) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const deadlineDate = new Date(deadline);
  deadlineDate.setHours(0, 0, 0, 0);
  
  const diffTime = deadlineDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Проверяет, нужно ли отправить уведомление
 * @param {Date} deadline - Дата дедлайна
 * @param {string} notificationType - Тип уведомления ('weekBefore', 'dayBefore', 'dayOf')
 * @returns {boolean} - true если нужно отправить уведомление
 */
export const shouldSendNotification = (deadline, notificationType) => {
  if (!deadline || !notificationType) {
    return false;
  }
  
  const daysUntil = getDaysUntilDeadline(deadline);
  
  if (daysUntil === null) {
    return false;
  }
  
  // Проверяем различные типы уведомлений
  switch (notificationType) {
    case 'dayOf':
      return daysUntil === 0;
    case 'dayBefore':
      return daysUntil === 1;
    case 'weekBefore':
      return daysUntil === 7;
    default:
      return false;
  }
};
